import { useCallback, useEffect, useState } from "react";
import { getSupabase } from "@dwelldrop/supabase";
import { useVenueAuth } from "../contexts/VenueAuthContext";

const VENUE_SUPABASE_STORAGE_KEY = "venuehub_venue_token";

// dpa has its own dedicated signing flow (VenueAccessGate / /dpa), so it is
// excluded from the consent modal gate.
const EXCLUDED_VENUE_CONSENT_NAMES = new Set(["dpa"]);

export type VenueConsentType = {
  id: string;
  name: string;
  description: string;
  version: number;
  is_required: boolean;
};

export type VenueActiveConsent = {
  consent_name: string;
  // get_user_consents left-joins consent_types, so a type the venue has never
  // acted on comes back with a null status rather than being absent.
  status: string | null;
  version: number;
};

export type VenueConsentData = {
  types: VenueConsentType[];
  consents: VenueActiveConsent[];
};

type UseVenueConsentGateReturn = {
  needsConsent: boolean;
  loading: boolean;
  consentData: VenueConsentData | null;
  refresh: () => Promise<void>;
};

type VenueConsentTypeWithClassification = VenueConsentType & {
  requires_reconsent: boolean;
};

// Mirrors consumer authContext.checkConsentVersions: Case A
// (requires_reconsent = false) auto-carries the existing consent forward
// instead of blocking with the forced-consent modal; Case B blocks.
// First-time users (no prior consent) have nothing to carry, so they still
// fall through to the mandatory prompt.
function classifyConsent(
  types: VenueConsentTypeWithClassification[],
  consents: VenueActiveConsent[],
): { needsConsent: boolean; toCarryForward: string[] } {
  if (types.length === 0) return { needsConsent: false, toCarryForward: [] };

  const activeByName = new Map<string, number>();
  for (const c of consents) {
    if (c.status === "active") {
      activeByName.set(c.consent_name, c.version);
    }
  }

  let needsConsent = false;
  const toCarryForward: string[] = [];

  for (const t of types) {
    const userVersion = activeByName.get(t.name);
    // Keep required-consent check mismatch-based to support legacy version history.
    const isOutdated = t.is_required
      ? userVersion === undefined || userVersion !== t.version
      : userVersion !== undefined && userVersion < t.version;

    if (!isOutdated) continue;

    if (!t.requires_reconsent && userVersion !== undefined) {
      toCarryForward.push(t.name);
    } else {
      needsConsent = true;
    }
  }

  return { needsConsent, toCarryForward };
}

export function useVenueConsentGate(enabled: boolean): UseVenueConsentGateReturn {
  const { user } = useVenueAuth();
  const [needsConsent, setNeedsConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [consentData, setConsentData] = useState<VenueConsentData | null>(null);

  const fetchConsent = useCallback(async (): Promise<void> => {
    if (!enabled || !user) {
      setNeedsConsent(false);
      setConsentData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const supabase = getSupabase(VENUE_SUPABASE_STORAGE_KEY);

    const [typesRes, consentsRes] = await Promise.all([
      supabase
        .from("consent_types")
        .select(
          "id, name, description, version, is_required, requires_reconsent, type",
        )
        .eq("type", "venue")
        .order("is_required", { ascending: false }),
      supabase.rpc("get_user_consents"),
    ]);

    const rawTypes = ((typesRes.data ?? []) as (VenueConsentTypeWithClassification & {
      type: string;
    })[]).filter((t) => !EXCLUDED_VENUE_CONSENT_NAMES.has(t.name));
    const safeTypes: VenueConsentType[] = rawTypes.map(
      ({ id, name, description, version, is_required }) => ({
        id,
        name,
        description,
        version,
        is_required,
      }),
    );
    let safeConsents = (consentsRes.data ?? []) as VenueActiveConsent[];

    // On query error (typically offline), don't block the UI — we can't tell
    // whether the user already accepted.
    if (typesRes.error || consentsRes.error) {
      setNeedsConsent(false);
      setConsentData({ types: safeTypes, consents: safeConsents });
      setLoading(false);
      return;
    }

    const { needsConsent: computedNeedsConsent, toCarryForward } =
      classifyConsent(rawTypes, safeConsents);

    if (toCarryForward.length > 0) {
      const results = await Promise.all(
        toCarryForward.map((name) =>
          supabase.rpc("carry_forward_consent", { p_consent_type_name: name }),
        ),
      );
      for (const r of results) {
        if (r.error) {
          console.error("Failed to carry forward consent:", r.error);
        }
      }
      const refreshed = await supabase.rpc("get_user_consents");
      if (!refreshed.error && refreshed.data) {
        safeConsents = refreshed.data as VenueActiveConsent[];
      }
    }

    setConsentData({ types: safeTypes, consents: safeConsents });
    setNeedsConsent(computedNeedsConsent);
    setLoading(false);
  }, [enabled, user]);

  useEffect(() => {
    void fetchConsent();
  }, [fetchConsent]);

  // Match consumer authContext: re-check when admin publishes consent_types bumps.
  useEffect(() => {
    if (!enabled || !user) return;

    const supabase = getSupabase(VENUE_SUPABASE_STORAGE_KEY);
    const channel = supabase
      .channel("venue-consent-types-watch")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "consent_types" },
        () => {
          void fetchConsent();
        },
      )
      .subscribe();

    return () => {
      void channel.unsubscribe();
    };
  }, [enabled, user, fetchConsent]);

  return { needsConsent, loading, consentData, refresh: fetchConsent };
}
